import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ShieldOutlined } from '@mui/icons-material';
import { mfaSetupBegin, mfaSetupConfirm, clearError, clearMfaState, logout } from '../../redux/slices/authSlice';
import AuthLayout from './AuthLayout';
import RecoveryCodes from './RecoveryCodes';

/**
 * First-time TOTP setup, forced after login for users where MFA is mandatory
 * (or chosen during opt-in). Steps: scan QR -> confirm code -> save recovery
 * codes -> continue into the app. Driven by the short-lived mfaToken.
 */
const MfaSetupPage = () => {
  const [provisioning, setProvisioning] = useState(null); // {qr_png_base64, secret, config_url}
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const beganRef = useRef(false);
  const { isLoading, error, mfaToken, isAuthenticated, user } = useSelector((state) => state.auth);

  // No setup in progress (direct nav / refresh) — back to login.
  useEffect(() => {
    if (!mfaToken && !isAuthenticated) {
      navigate('/auth/login');
    }
  }, [mfaToken, isAuthenticated, navigate]);

  // Provision a device once, on entry.
  useEffect(() => {
    if (beganRef.current || !mfaToken) return;
    beganRef.current = true;
    (async () => {
      const result = await dispatch(mfaSetupBegin({ mfaToken }));
      if (result.meta.requestStatus === 'fulfilled') {
        setProvisioning(result.payload);
      }
    })();
  }, [dispatch, mfaToken]);

  const handleConfirm = async (e) => {
    e.preventDefault();
    const otp = code.trim();
    if (!otp) return;
    const result = await dispatch(mfaSetupConfirm({ otp, mfaToken }));
    if (result.meta.requestStatus === 'fulfilled') {
      // Show the one-time recovery codes before entering the app.
      setRecoveryCodes(result.payload.recoveryCodes || []);
    }
  };

  const handleContinue = () => {
    // mfaSetupConfirm already authenticated us (login-time setup).
    navigate(user?.must_change_password ? '/auth/change-password' : '/');
  };

  const handleCancel = () => {
    dispatch(clearMfaState());
    dispatch(logout());
    navigate('/auth/login');
  };

  return (
    <AuthLayout>
      <Box
        sx={{
          width: '100%',
          maxWidth: 460,
          bgcolor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          p: { xs: 3.5, sm: 4.5 },
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Box
            sx={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 56, height: 56, borderRadius: '50%', bgcolor: '#E3F2FD', mb: 1,
            }}
          >
            <ShieldOutlined sx={{ fontSize: 28, color: '#1565C0' }} />
          </Box>
        </Box>

        {recoveryCodes ? (
          <>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#212529', textAlign: 'center', mb: 0.5 }}>
              Save Your Recovery Codes
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#6c757d', textAlign: 'center', mb: 3 }}>
              Store these somewhere safe. Each code can be used once if you lose access to your authenticator app.
            </Typography>
            <RecoveryCodes codes={recoveryCodes} />
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleContinue}
              sx={{
                mt: 3, py: 1.5, fontSize: '0.95rem', fontWeight: 600, textTransform: 'none',
                borderRadius: '28px', bgcolor: '#2C3E50', boxShadow: 'none',
                '&:hover': { bgcolor: '#1A242F', boxShadow: '0 4px 12px rgba(44,62,80,0.3)' },
              }}
            >
              I've saved my codes — Continue
            </Button>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: '#212529', textAlign: 'center', mb: 0.5 }}>
              Set Up Two-Factor Authentication
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', color: '#6c757d', textAlign: 'center', mb: 3 }}>
              Scan the QR code with Google Authenticator, Microsoft Authenticator, or Bitwarden,
              then enter the 6-digit code to confirm.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2.5, borderRadius: 1.5 }} onClose={() => dispatch(clearError())}>
                {typeof error === 'object' ? error.detail || JSON.stringify(error) : error}
              </Alert>
            )}

            {provisioning ? (
              <>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  <Box
                    component="img"
                    alt="Authenticator QR code"
                    src={`data:image/png;base64,${provisioning.qr_png_base64}`}
                    sx={{ width: 200, height: 200, border: '1px solid #e9ecef', borderRadius: 2, p: 1 }}
                  />
                </Box>
                <Typography sx={{ fontSize: '0.75rem', color: '#6c757d', textAlign: 'center', mb: 0.5 }}>
                  Can't scan? Enter this key manually:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'monospace', fontSize: '0.85rem', textAlign: 'center',
                    wordBreak: 'break-all', color: '#212529', bgcolor: '#f8f9fa',
                    borderRadius: 1, p: 1, mb: 3,
                  }}
                >
                  {provisioning.secret}
                </Typography>

                <form onSubmit={handleConfirm}>
                  <TextField
                    fullWidth
                    name="otp"
                    label="6-digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    disabled={isLoading}
                    autoFocus
                    autoComplete="one-time-code"
                    inputProps={{ inputMode: 'numeric', style: { letterSpacing: '0.3em', textAlign: 'center', fontSize: '1.2rem' } }}
                    sx={{
                      mb: 3,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        '&:hover fieldset': { borderColor: '#2C3E50' },
                        '&.Mui-focused fieldset': { borderColor: '#2C3E50' },
                      },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#2C3E50' },
                    }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isLoading || !code.trim()}
                    sx={{
                      py: 1.5, fontSize: '0.95rem', fontWeight: 600, textTransform: 'none',
                      borderRadius: '28px', bgcolor: '#2C3E50', boxShadow: 'none',
                      '&:hover': { bgcolor: '#1A242F', boxShadow: '0 4px 12px rgba(44,62,80,0.3)' },
                      '&:disabled': { bgcolor: '#2C3E50', opacity: 0.7, color: '#fff' },
                    }}
                  >
                    {isLoading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Confirm & Enable'}
                  </Button>
                </form>
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#2C3E50' }} />
              </Box>
            )}

            <Box sx={{ textAlign: 'center', mt: 2.5 }}>
              <Typography
                onClick={handleCancel}
                sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#3498db', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              >
                Cancel and sign out
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </AuthLayout>
  );
};

export default MfaSetupPage;
